import { ReactNode, Suspense, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SuspendedAccountScreen } from "@/components/SuspendedAccountScreen";

interface AgencyRouteProps {
  children: ReactNode;
}

// Per-feature Suspense + ErrorBoundary so a failed lazy chunk or a render
// crash in any /agency/* page is scoped to the agency tree and doesn't blank
// the entire app shell.
const AgencyFeatureFallback = () => (
  <div className="min-h-dvh flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);


/**
 * Route guard for agency-only pages.
 * - Allows admin users to bypass all checks (for white-glove agency management)
 * - Redirects unauthenticated users to /auth
 * - Redirects client users (non-agency) to /dashboard
 * - Redirects agency users without completed onboarding to /agency/onboarding
 */
export function AgencyRoute({ children }: AgencyRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [profileLoading, setProfileLoading] = useState(true);
  const [userType, setUserType] = useState<string | null>(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [agencySuspended, setAgencySuspended] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setProfileLoading(false);
        return;
      }

      // Check profile and admin status in parallel
      const [profileRes, adminRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("user_type, agency_id, onboarding_completed")
          .eq("id", user.id)
          .maybeSingle(),
        supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }),
      ]);

      setUserType(profileRes.data?.user_type || null);
      setIsAdmin(!!adminRes.data);

      // Invited agents join an existing agency and never run the creator
      // onboarding wizard. If their profile is linked to an agency that
      // already has other members, treat them as onboarded so password-reset /
      // re-login doesn't bounce them back to /agency/onboarding indefinitely.
      let completed = profileRes.data?.onboarding_completed ?? false;
      const agencyId = profileRes.data?.agency_id;
      if (!completed && agencyId) {
        const { count } = await supabase
          .from("agency_members")
          .select("id", { count: "exact", head: true })
          .eq("agency_id", agencyId);
        if ((count ?? 0) > 1) {
          completed = true;
        }
      }
      setOnboardingCompleted(completed);

      // Suspension check: a suspended agency's members are blocked from the
      // backend entirely (platform admins bypass above).
      if (agencyId && !adminRes.data) {
        const { data: agency } = await supabase
          .from("agencies")
          .select("approval_status")
          .eq("id", agencyId)
          .maybeSingle();
        setAgencySuspended(agency?.approval_status === "suspended");
      }

      setProfileLoading(false);
    };

    if (!authLoading) {
      fetchProfile();
    }
  }, [user, authLoading]);

  // Show loading spinner while checking auth/profile
  if (authLoading || profileLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Not logged in → redirect to auth
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Admin users can access any agency backend
  if (isAdmin) {
    return (
      <ErrorBoundary key={location.pathname} scope="agency">
        <Suspense fallback={<AgencyFeatureFallback />}>{children}</Suspense>
      </ErrorBoundary>
    );
  }

  // Client user (not agency) → redirect to client dashboard
  if (userType !== "agency") {
    return <Navigate to="/dashboard" replace />;
  }

  // Suspended agency → full-screen suspended state, no backend access
  if (agencySuspended) {
    return <SuspendedAccountScreen variant="agency" />;
  }

  // Agency user but onboarding not completed → redirect to onboarding
  const isOnboardingPath = location.pathname === "/agency/onboarding" || location.pathname === "/agency/signup";
  if (!onboardingCompleted && !isOnboardingPath) {
    return <Navigate to="/agency/onboarding" replace />;
  }

  return (
    <ErrorBoundary key={location.pathname} scope="agency">
      <Suspense fallback={<AgencyFeatureFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

export default AgencyRoute;

